import { useCallback, useMemo } from 'react';
import { isAxiosError } from 'axios';

import { useBrowserPushSubscription } from 'hooks/useBrowserPushSubscription';
import { useNotificationPermission } from 'hooks/useNotificationPermission';
import { usePushDevices } from 'hooks/usePushDevices';
import { isPushSupported, isStandalone, PushPermission, subscribeBrowser, subscriptionToParams } from 'lib/webPush';

type Options = {
  // Off = the hook touches nothing: no service worker, no API call. Callers pass the flag.
  enabled?: boolean;
};

/**
 * Web Push for this device (OLIO-3563, PWA Push POC), composed from three sources of truth: the
 * browser's notification permission, the browser's own PushSubscription, and the API's device rows.
 * "This device" is the row whose endpoint matches the browser's subscription. A browser subscription
 * the API has no row for (a rotated endpoint, a row deleted elsewhere, another user's leftover in
 * this browser) simply reads as "not enabled"; Enable re-subscribes — the browser hands back the same
 * subscription — and the API's upsert creates or transfers the row.
 */
export function usePushSubscription({ enabled = true }: Options = {}) {
  const supported = isPushSupported();
  const active = enabled && supported;

  const { permission, setPermission } = useNotificationPermission({ active });
  const browser = useBrowserPushSubscription({ active });
  const api = usePushDevices({ active });

  const thisDevice = useMemo(
    () => api.devices.find((device) => device.endpoint === browser.subscription?.endpoint) ?? null,
    [api.devices, browser.subscription]
  );

  // Step 1 of enabling: the browser's permission prompt. Only ever called from a click — browsers
  // refuse to show the prompt outside a user gesture — and the answer is stored whatever it is.
  const requestPermission = useCallback(async (): Promise<PushPermission> => {
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, [setPermission]);

  // Step 2: ask the browser for a subscription bound to our server key, and remember it. When the
  // push service has rejected this device's endpoint (the row is dead), the browser would hand the
  // same dead subscription straight back and the row would be revived only to fail again — so
  // unsubscribe first and let the browser mint a fresh endpoint.
  const subscribeThisDevice = useCallback(
    async (vapidPublicKey: string) => {
      if (browser.subscription && thisDevice?.failedAt) await browser.subscription.unsubscribe();
      const subscription = await subscribeBrowser(vapidPublicKey);
      browser.setSubscription(subscription);
      return subscription;
    },
    [browser, thisDevice]
  );

  // Step 3: tell the API about the device. Re-registering a subscription the API already knows for
  // this user is a safe upsert. A dead row replaced by a fresh endpoint is removed, so the device
  // list does not keep the corpse.
  const registerThisDevice = useCallback(
    async (subscription: PushSubscription) => {
      const row = await api.register.mutateAsync(subscriptionToParams(subscription));
      if (thisDevice?.failedAt && thisDevice.id !== row.id) await api.remove.mutateAsync(thisDevice.id);
      return row;
    },
    [api.register, api.remove, thisDevice]
  );

  // A 409 means the browser's subscription is registered to someone else — a previous user of this
  // workstation. The API never transfers a row on an endpoint alone (an endpoint is no proof of
  // holding the browser), but we ARE the browser: release the leftover subscription, let the browser
  // mint a fresh endpoint, and register that. The previous owner's row dies on its next send.
  const replaceLeftoverSubscription = useCallback(
    async (leftover: PushSubscription, vapidPublicKey: string) => {
      await leftover.unsubscribe();
      const fresh = await subscribeBrowser(vapidPublicKey);
      browser.setSubscription(fresh);
      await api.register.mutateAsync(subscriptionToParams(fresh));
    },
    [browser, api.register]
  );

  const enable = useCallback(async (): Promise<PushPermission> => {
    if (!supported) return 'unsupported';
    if (!api.vapidPublicKey) throw new Error('Push is not configured for this environment.');

    const permission = await requestPermission();
    if (permission !== 'granted') return permission;

    const subscription = await subscribeThisDevice(api.vapidPublicKey);
    try {
      await registerThisDevice(subscription);
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 409) throw error;
      await replaceLeftoverSubscription(subscription, api.vapidPublicKey);
    }
    return permission;
  }, [
    supported,
    api.vapidPublicKey,
    requestPermission,
    subscribeThisDevice,
    registerThisDevice,
    replaceLeftoverSubscription,
  ]);

  // Removes both halves, browser first: unsubscribing is local and kills the endpoint, so nothing
  // can be delivered here even if the row delete then fails (a dead row is marked on its next send).
  const disable = useCallback(async () => {
    if (browser.subscription) {
      await browser.subscription.unsubscribe();
      browser.setSubscription(null);
    }
    if (thisDevice) await api.remove.mutateAsync(thisDevice.id);
  }, [thisDevice, browser, api.remove]);

  return {
    supported,
    standalone: supported && isStandalone(),
    permission,
    vapidConfigured: api.isSuccess && api.vapidPublicKey !== null,
    loading: active && (api.isPending || browser.isPending),
    loadError: api.error,
    devices: api.devices,
    thisDevice,
    enabledOnThisDevice: thisDevice !== null && thisDevice.failedAt === null,
    enable,
    disable,
    sendTest: api.sendTest.mutateAsync,
    isEnabling: api.register.isPending,
    isDisabling: api.remove.isPending,
    isSending: api.sendTest.isPending,
  };
}
